import React, { useCallback, useMemo, useState } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Empty, Segmented, Space, Spin, Tabs, Input, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { pickPageName, pickPortalName } from '../../../utils/dataPortalCache';
import { useFetchDataPortal } from '../../../hooks/useFetchDataPortal';
import BoardCard from './components/BoardCard';
import BoardGroupSection from './components/BoardGroupSection';
import DataPortalPageDrawer from './components/DataPortalPageDrawer';
import DeleteBoardModal from './components/DeleteBoardModal';
import GroupTable from './components/GroupTable';
import MoveToGroupModal from './components/MoveToGroupModal';
import {
    CREATE_DATA_PORTAL_PAGE,
    DATA_PORTAL_SEARCH_PAGES,
    DELETE_DATA_PORTAL_PAGE,
    DataPortalGroupType,
    DataPortalOverviewType,
    DataPortalPageType,
    GET_DATA_PORTAL_GROUPS,
    GET_DATA_PORTAL_OVERVIEW,
    GET_PORTAL_CODES,
    MOVE_PAGES_TO_GROUP,
    UPDATE_DATA_PORTAL_PAGE,
} from './graphql/queries';

type StatusFilter = 'all' | 'on' | 'off';

/** 「未分组」段的折叠 key。带门户后缀，否则多个门户的未分组段会互相牵连着一起展开 */
const ungroupedKeyOf = (portalCode: string) => `__ungrouped__${portalCode}`;

/**
 * 看板管理页（配置白名单内可见）。
 *
 * 配置方式：商分同学在门户页面点「分享」生成嵌入链接，管理员把链接粘到这里。
 * 后端校验域名白名单后只存路径，域名按环境拼——所以一份配置数据 test/prod 通用。
 *
 * **没有「一次拉全部看板」的查询**：默认只拉分组列表与每组计数，展开某组才查该组
 * （见 `BoardGroupSection`）；概览三个数字走独立 COUNT 聚合。这样加载量与分组数
 * 而不是看板总数成正比，看板涨到几百也不会一次把全部卡片拉回来。
 * 搜索/状态筛选态下改走后端跨分组查询，平铺展示命中项。
 */
const DataPortalManagePage: React.FC = () => {
    const { t, i18n } = useTranslation();

    const [tab, setTab] = useState<'boards' | 'groups'>('boards');
    const [rawKeyword, setRawKeyword] = useState('');
    const [keyword, setKeyword] = useState('');
    const [status, setStatus] = useState<StatusFilter>('all');
    const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string> | null>(null);
    // 折叠的门户段（默认全展开）。只在多门户时有意义——单门户没有门户标题、无折叠入口
    const [collapsedPortals, setCollapsedPortals] = useState<Set<string>>(new Set());
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loadedPages, setLoadedPages] = useState<DataPortalPageType[]>([]);
    const [refreshToken, setRefreshToken] = useState(0);

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [editing, setEditing] = useState<DataPortalPageType | null>(null);
    const [movingPage, setMovingPage] = useState<DataPortalPageType | null>(null);
    const [batchMoving, setBatchMoving] = useState(false);
    const [deletingPage, setDeletingPage] = useState<DataPortalPageType | null>(null);

    const enabledFilter = status === 'all' ? undefined : status === 'on';
    const searchMode = keyword.trim().length > 0 || status !== 'all';

    const { data: groupData, loading: groupLoading, refetch: refetchGroups } = useQuery<{
        dataPortalGroups: DataPortalGroupType[];
    }>(GET_DATA_PORTAL_GROUPS, { fetchPolicy: 'network-only' });

    const { data: overviewData, refetch: refetchOverview } = useQuery<{
        dataPortalOverview: DataPortalOverviewType;
    }>(GET_DATA_PORTAL_OVERVIEW, { fetchPolicy: 'network-only' });

    // 可选来源门户：拿不到就退化成只有 BA，不阻断配置。
    // 后端已按 sortOrder 排好序，直接按这个顺序渲染门户分段，不在前端再排
    const { data: portalCodeData } = useQuery<{ dataPortalPortalCodes: string[] }>(GET_PORTAL_CODES, {
        fetchPolicy: 'cache-first',
        onError: (e) => console.warn('Fetch portal codes failed', e),
    });
    const portalCodes = useMemo(
        () => portalCodeData?.dataPortalPortalCodes ?? ['BA'],
        [portalCodeData],
    );

    const { data: searchData, loading: searchLoading, refetch: refetchSearch } = useQuery<{
        dataPortalSearchPages: DataPortalPageType[];
    }>(DATA_PORTAL_SEARCH_PAGES, {
        variables: { keyword: keyword.trim() || null, enabled: enabledFilter ?? null },
        skip: !searchMode,
        fetchPolicy: 'network-only',
        onError: (e) => console.warn('Search data portal pages failed', e),
    });

    const [createPage] = useMutation(CREATE_DATA_PORTAL_PAGE);
    const [updatePage] = useMutation(UPDATE_DATA_PORTAL_PAGE);
    const [deletePage] = useMutation(DELETE_DATA_PORTAL_PAGE);
    const [movePages] = useMutation(MOVE_PAGES_TO_GROUP);

    // 保存后要刷新用户侧缓存，否则左侧二级栏还是旧的，管理员得手动刷页面才看得到。
    // 用轻量的 refreshMenu（只重拉菜单、不重查灰度）：管理操作不会翻转灰度开关，省一次慢查询
    const { refreshMenu } = useFetchDataPortal();

    // 用 useMemo 而不是直接 `?? []`：后者每次渲染都产生新数组引用，会让下面依赖它的
    // useMemo / useCallback 每次都重算
    const groups = useMemo(() => groupData?.dataPortalGroups ?? [], [groupData]);
    const overview = overviewData?.dataPortalOverview;
    const searchResults = useMemo(() => searchData?.dataPortalSearchPages ?? [], [searchData]);

    /**
     * 默认只展开第一个分组（与浏览页二级栏一致的观感）。
     *
     * 分段 key 在门户分层下带门户后缀，所以默认展开项取「第一个门户的第一个分组」，
     * 与页面上视觉第一项一致；该门户没有分组时退化到它的未分组段。
     */
    const expanded = useMemo(() => {
        if (expandedGroupIds) return expandedGroupIds;
        const firstPortal = portalCodes[0];
        const firstGroup = groups.find((g) => g.portalCode === firstPortal) ?? groups[0];
        return new Set<string>([firstGroup?.id ?? ungroupedKeyOf(firstPortal)]);
    }, [expandedGroupIds, groups, portalCodes]);

    /** 取某门户的未分组看板数。该门户一个未分组看板都没有时后端不下发这一项，查不到即 0 */
    const ungroupedCountOf = (portalCode: string) =>
        overview?.ungroupedByPortal?.find((item) => item.portalCode === portalCode)?.count ?? 0;

    const refreshAll = useCallback(async () => {
        setRefreshToken((v) => v + 1);
        await Promise.all([
            refetchGroups(),
            refetchOverview(),
            searchMode ? refetchSearch() : Promise.resolve(),
        ]);
        await refreshMenu().catch((e) => console.warn('Refresh data portal menu failed', e));
    }, [refetchGroups, refetchOverview, refetchSearch, searchMode, refreshMenu]);

    /**
     * 合并某个分段加载到的看板，而不是整体覆盖。
     *
     * 原来是直接 `setLoadedPages`，多个分段同时展开时只留下最后加载的那一段 ——
     * 依赖这份数据的两处都会因此失准：抽屉里「当前分组内排第 N 位」的提示，
     * 以及批量移动时的跨门户判定。按 id 去重合并即可，展开的分段数量级很小。
     */
    const mergeLoadedPages = useCallback((pages: DataPortalPageType[]) => {
        setLoadedPages((prev) => {
            const byId = new Map(prev.map((page) => [page.id, page]));
            pages.forEach((page) => byId.set(page.id, page));
            return [...byId.values()];
        });
    }, []);

    const toggleExpand = (groupId: string) => {
        const next = new Set(expanded);
        if (next.has(groupId)) {
            next.delete(groupId);
        } else {
            next.add(groupId);
        }
        setExpandedGroupIds(next);
    };

    /** 折叠/展开整个门户段（连同它下面的未分组与各分组一起收起） */
    const togglePortal = (portalCode: string) => {
        setCollapsedPortals((prev) => {
            const next = new Set(prev);
            if (next.has(portalCode)) {
                next.delete(portalCode);
            } else {
                next.add(portalCode);
            }
            return next;
        });
    };

    const toggleSelect = (id: string) =>
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));

    const openCreate = () => {
        setEditing(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    const openEdit = (page: DataPortalPageType) => {
        setEditing(page);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    const handleSubmit = async (values: any) => {
        const input = {
            // 新增时留空由后端生成简短随机串；编辑时原样回传
            pageKey: editing?.pageKey ?? '',
            portalCode: values.portalCode ?? 'BA',
            groupId: values.groupId ?? null,
            // 三语名称至少填一个，未填的提交空串而不是 null——
            // GraphQL Input 里是 String! 非空类型，传 null 会被拒（coerced Null value）
            nameEn: values.nameEn ?? '',
            nameZh: values.nameZh ?? '',
            nameEs: values.nameEs ?? '',
            // 后端会校验域名白名单并只存路径，这里原样提交管理员粘的内容
            embedPath: values.embedPath,
            icon: values.icon ?? null,
            sortOrder: Number(values.sortOrder ?? 0),
            // 可见范围必填（表单已校验），不再有 ALL 兜底
            visibleScope: values.visibleScope,
            // 只保留当前 scope 对应维度的值，另一维度提交空，避免库里残留会误导人的旧配置
            visibleRoles: values.visibleScope === 'ROLE' ? (values.visibleRoles ?? []) : [],
            visibleAccountKey: values.visibleAccountKey ?? 'ACCOUNT_ID',
            visibleAccounts: values.visibleScope === 'ACCOUNT' ? (values.visibleAccounts ?? []) : [],
            restrictConfigVisible: !!values.restrictConfigVisible,
            openMode: values.openMode ?? 'IFRAME',
            enabled: !!values.enabled,
        };
        try {
            if (drawerMode === 'edit' && editing?.id) {
                await updatePage({ variables: { input: { ...input, id: editing.id } } });
                message.success(t('central.dataPortalConfig.messages.updated') as string);
            } else {
                await createPage({ variables: { input } });
                message.success(t('central.dataPortalConfig.messages.created') as string);
            }
            setDrawerVisible(false);
            await refreshAll();
        } catch (e: any) {
            message.error(e.message);
        }
    };

    const handleDelete = async () => {
        if (!deletingPage) return;
        try {
            await deletePage({ variables: { id: deletingPage.id } });
            message.success(t('central.dataPortalConfig.messages.deleted') as string);
            setDeletingPage(null);
            await refreshAll();
        } catch (e: any) {
            message.error(e.message);
        }
    };

    const handleMove = async (groupId: string | null) => {
        const ids = batchMoving ? selectedIds : movingPage ? [movingPage.id] : [];
        if (ids.length === 0) return;
        try {
            await movePages({ variables: { ids, groupId } });
            message.success(t('central.dataPortalConfig.messages.moved') as string);
            setMovingPage(null);
            setBatchMoving(false);
            setSelectedIds([]);
            await refreshAll();
        } catch (e: any) {
            message.error(e.message);
        }
    };

    const applyKeyword = () => setKeyword(rawKeyword);

    /**
     * 待移动看板的门户。批量移动时选中项**跨了门户**就返回 null ——
     * 此时不存在任何合法的目标分组，弹窗据此只留「退回未分组」并给出提示。
     *
     * 批量时的选中项从「已加载的看板」里找（`loadedPages` 已改为跨分段累积合并，
     * 见 [mergeLoadedPages]）。查不到就保守当作跨门户，不猜。
     */
    const movingPortalCode = useMemo(() => {
        if (!batchMoving) return movingPage?.portalCode ?? null;
        const pool = searchMode ? searchResults : loadedPages;
        const codes = selectedIds.map((id) => pool.find((page) => page.id === id)?.portalCode);
        // 有任何一个选中项查不到门户就当作跨门户处理：宁可多提示一次「请按门户分批」，
        // 也不要基于不完整数据给出一批看似可选、实际会被后端拒绝的目标分组
        if (codes.some((code) => code == null)) return null;
        return new Set(codes).size === 1 ? codes[0]! : null;
    }, [batchMoving, movingPage, selectedIds, searchMode, searchResults, loadedPages]);

    const cardHandlers = {
        onToggleSelect: toggleSelect,
        onEdit: openEdit,
        onMove: (page: DataPortalPageType) => setMovingPage(page),
        onDelete: (page: DataPortalPageType) => setDeletingPage(page),
    };

    const boardsView = (
        <div>
            <div className="flex items-center justify-between gap-3 mb-3">
                <Space>
                    <Input.Search
                        allowClear
                        value={rawKeyword}
                        onChange={(e) => setRawKeyword(e.target.value)}
                        onSearch={applyKeyword}
                        placeholder={t('central.dataPortalConfig.placeholders.searchBoard') as string}
                        style={{ width: 240 }}
                    />
                    <Segmented
                        value={status}
                        onChange={(value) => setStatus(value as StatusFilter)}
                        options={[
                            { value: 'all', label: t('central.dataPortalConfig.statusFilter.all') },
                            { value: 'on', label: t('enum.activeStatus.active') },
                            { value: 'off', label: t('enum.activeStatus.suspended') },
                        ]}
                    />
                </Space>
                <Space>
                    {selectedIds.length > 0 && (
                        <>
                            <span className="text-sm text-gray-500">
                                {t('central.dataPortalConfig.selectedCount', { count: selectedIds.length })}
                            </span>
                            <Button onClick={() => setBatchMoving(true)}>
                                {t('central.dataPortalConfig.actions.moveToGroup')}
                            </Button>
                            <Button type="text" onClick={() => setSelectedIds([])}>
                                {t('components.button.cancel')}
                            </Button>
                        </>
                    )}
                    {/* 新建看板与「分组」tab 的新建分组同层级：都放在各自 tab 内容区右上，只在对应 tab 出现 */}
                    <Button type="primary" onClick={openCreate}>
                        {t('central.dataPortalConfig.actions.newBoard')}
                    </Button>
                </Space>
            </div>

            {searchMode ? (
                searchLoading ? (
                    <div className="py-8 flex justify-center">
                        <Spin />
                    </div>
                ) : searchResults.length === 0 ? (
                    <Empty description={t('central.dataPortalConfig.noMatchBoard')} />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                        {searchResults.map((page) => (
                            <BoardCard key={page.id} page={page} selected={selectedIds.includes(page.id)} {...cardHandlers} />
                        ))}
                    </div>
                )
            ) : (
                <div>
                    {/* 门户 → 分组 → 卡片 三层。portalCodes 已只含当前用户能配的门户（后端过滤），
                        所以这里不再叠一层权限判定。
                        **只有多于一个门户时才渲染门户标题**：单门户的人（商分/产品）标题不携带任何
                        信息——他能配的就只有这一个门户，加个标题只是多占一行、多一道视觉分隔。 */}
                    {portalCodes.map((portalCode) => {
                        // 门户标题只在多门户时出现；单门户没标题、也就没有折叠入口，恒展开
                        const collapsible = portalCodes.length > 1;
                        const portalCollapsed = collapsible && collapsedPortals.has(portalCode);
                        return (
                            <div key={portalCode} className="mb-6">
                                {collapsible && (
                                    // 门户是上层容器：字号(text-base)、字重(semibold)、颜色都比分组标题(text-sm/medium)高一档，
                                    // 拉开层级感；折叠图标也随之放大
                                    <div
                                        className="pb-2 mb-2 border-b border-gray-200 flex items-center gap-2 cursor-pointer select-none"
                                        onClick={() => togglePortal(portalCode)}
                                    >
                                        {portalCollapsed ? (
                                            <RightOutlined className="text-sm text-gray-500" />
                                        ) : (
                                            <DownOutlined className="text-sm text-gray-500" />
                                        )}
                                        <span className="text-base font-semibold text-gray-900">
                                            {pickPortalName(portalCode, t)}
                                        </span>
                                    </div>
                                )}
                                {!portalCollapsed && (
                                    // 门户下的分组整体左缩进，体现「这些分组从属于上面的门户」；单门户无门户标题、不缩进
                                    <div className={collapsible ? 'pl-5' : ''}>
                                        {/* 未分组段置顶：带橙色标签催归类。计数按门户取，不是全局总数 */}
                                        <BoardGroupSection
                                            groupId={null}
                                            portalCode={portalCode}
                                            title={t('pages.dataPortal.ungrouped')}
                                            enabledCount={ungroupedCountOf(portalCode)}
                                            expanded={expanded.has(ungroupedKeyOf(portalCode))}
                                            onToggle={() => toggleExpand(ungroupedKeyOf(portalCode))}
                                            enabledFilter={enabledFilter}
                                            selectedIds={selectedIds}
                                            onLoaded={mergeLoadedPages}
                                            refreshToken={refreshToken}
                                            {...cardHandlers}
                                        />
                                        {groups
                                            .filter((group) => group.portalCode === portalCode)
                                            .map((group) => (
                                                <BoardGroupSection
                                                    key={group.id}
                                                    groupId={group.id}
                                                    title={pickPageName(group, i18n.language)}
                                                    enabledCount={group.enabledPageCount}
                                                    expanded={expanded.has(group.id)}
                                                    onToggle={() => toggleExpand(group.id)}
                                                    enabledFilter={enabledFilter}
                                                    selectedIds={selectedIds}
                                                    onLoaded={mergeLoadedPages}
                                                    refreshToken={refreshToken}
                                                    {...cardHandlers}
                                                />
                                            ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    return (
        <div className="bg-white rounded-lg shadow-sm px-4 pb-4 pt-3 flex-1 min-h-0 overflow-y-auto">
            {/* 页头只放标题 + 概览；新建按钮下沉到各自 tab 内容区，与「新建分组」同层级 */}
            <div className="mb-3">
                <div className="text-xl font-medium">{t('central.dataPortalConfig.title')}</div>
                {overview && (
                    <div className="text-xs text-gray-400 mt-1">
                        {t('central.dataPortalConfig.overview', {
                            total: overview.total,
                            enabled: overview.enabledCount,
                            ungrouped: overview.ungroupedCount,
                        })}
                    </div>
                )}
            </div>

            <Tabs
                activeKey={tab}
                onChange={(key) => {
                    setTab(key as 'boards' | 'groups');
                    // 切 tab 清空看板多选态，避免切到分组再切回来还留着上次的勾选
                    setSelectedIds([]);
                }}
                items={[
                    {
                        key: 'boards',
                        label: `${t('central.dataPortalConfig.tabs.boards')} ${overview?.total ?? 0}`,
                        children: boardsView,
                    },
                    {
                        key: 'groups',
                        label: `${t('central.dataPortalConfig.tabs.groups')} ${groups.length}`,
                        children: groupLoading ? (
                            <div className="py-8 flex justify-center">
                                <Spin />
                            </div>
                        ) : (
                            <GroupTable
                                groups={groups}
                                ungroupedCount={overview?.ungroupedCount ?? 0}
                                portalCodes={portalCodes}
                                onChanged={refreshAll}
                            />
                        ),
                    },
                ]}
            />

            <DataPortalPageDrawer
                visible={drawerVisible}
                mode={drawerMode}
                initialValues={editing}
                portalCodes={portalCodes}
                groups={groups}
                siblings={searchMode ? searchResults : loadedPages}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleSubmit}
            />

            <MoveToGroupModal
                open={movingPage != null || batchMoving}
                subject={
                    batchMoving
                        ? (t('central.dataPortalConfig.selectedCount', { count: selectedIds.length }) as string)
                        : movingPage
                          ? pickPageName(movingPage, i18n.language)
                          : ''
                }
                groups={groups}
                currentGroupId={batchMoving ? undefined : movingPage?.groupId ?? null}
                portalCode={movingPortalCode}
                onCancel={() => {
                    setMovingPage(null);
                    setBatchMoving(false);
                }}
                onSelect={handleMove}
            />

            <DeleteBoardModal
                open={deletingPage != null}
                page={deletingPage}
                onCancel={() => setDeletingPage(null)}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default DataPortalManagePage;
